import React, { FunctionComponent, useCallback, useState, useMemo } from 'react';
import { StateValues } from 'react-use-form-state';
import { AdminApi, AdminProductCreationRequest, AdminProductDetail, DocumentsApi } from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { ActionButton } from 'src/ui/molecules/action-buttons';
import { toMoney } from 'src/ui/helper/money';
import AddProductModal, { AddProductFields } from '../add-product-modal';
import useGoTo from 'src/hooks/use-go-to';
import { ISSUER_ROUTES } from '../../routes.config';

interface AddProductProps {
  onAddNewProduct: () => void;
  onClose: () => void;
}

const AddProduct: FunctionComponent<AddProductProps> = ({ onAddNewProduct, onClose }) => {
  const [isInfoView, setInfoView] = useState(true);
  const [isExitingView, setExitingView] = useState(false);
  const [isSuccessView, setSuccessView] = useState(false);
  const [productCreated, setProductCreated] = useState<AdminProductDetail>();

  const { makeAuthenticatedApi, withApi, loading: apiLoading, error } = useApiCall();

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const goToProductDetails = useGoTo(ISSUER_ROUTES.productDetails);

  const onCloseModal = useCallback(() => {
    if (isSuccessView) {
      setExitingView(false);
      onClose();
    }
    if (isInfoView) {
      setInfoView(false);
      onClose();
    }
    if (!isInfoView && !isExitingView && !isSuccessView) setExitingView(true);
    if (isExitingView) setExitingView(false);
  }, [isInfoView, isExitingView, isSuccessView]);

  const modalHeader = useMemo(() => {
    return isInfoView ? (
      <Translate name="addProduct.info.title" />
    ) : isExitingView ? (
      <Translate name="addProduct.exit.title" />
    ) : (
      <Translate name="addProduct.add" />
    );
  }, [isInfoView, isExitingView]);

  const actionButtons = useMemo((): ActionButton[] => {
    if (isInfoView) {
      return [
        {
          name: 'cancel',
          content: <Translate name="common.cancel" />,
          size: 'large',
          onClick: () => onClose(),
        },
        {
          name: 'start',
          content: <Translate name="common.continue" />,
          size: 'large',
          variant: 'primary',
          onClick: () => onStart(),
        },
      ];
    } else if (isExitingView) {
      return [
        {
          name: 'continue',
          content: <Translate name="addProduct.exit.continue" />,
          size: 'large',
          onClick: () => setExitingView(false),
        },
        {
          name: 'exit',
          content: <Translate name="addProduct.exit.button" />,
          variant: 'primary',
          onClick: () => onClose(),
          size: 'large',
        },
      ];
    } else if (isSuccessView) {
      return [
        {
          name: 'close',
          content: <Translate name="common.cancel" />,
          size: 'large',
          onClick: () => onCloseModal(),
        },
        {
          name: 'confirm',
          content: <Translate name="addProduct.success.button" />,
          variant: 'primary',
          onClick: () => goToProductDetails({ productId: productCreated?.id }),
          size: 'large',
        },
      ];
    } else {
      return [
        {
          name: 'cancel',
          content: <Translate name="common.cancel" />,
          size: 'large',
          onClick: () => onCloseModal(),
        },
        {
          name: 'add',
          content: <Translate name="common.add" />,
          variant: 'primary',
          loading: apiLoading,
          size: 'large',
          type: 'submit',
        },
      ];
    }
  }, [isInfoView, isExitingView, apiLoading]);

  const onSubmit = useCallback(
    (fieldsValues: StateValues<AddProductFields>, dateAndTime?: Date) => {
      withApi(async () => {
        const params: AdminProductCreationRequest = {
          ...fieldsValues,
          issuanceDocumentId: '',
          lockInTime: undefined,
          code: fieldsValues.assetCode,
          maxIssuableAmount: Number(fieldsValues.maxIssuableAmount),
          stepSize: Number(fieldsValues.stepSize),
          paymentDeadline: Number(fieldsValues.paymentDeadline),
          cancellationDeadline: Number(fieldsValues.cancellationDeadline),
          calculationFactor: Number(fieldsValues.calculationFactor),
          provideNominalValue: fieldsValues.provideNominalValue === 'true',
          nominalValue:
            fieldsValues.nominalValue && fieldsValues.provideNominalValue === 'true'
              ? typeof fieldsValues.nominalValue === 'number' || typeof fieldsValues.nominalValue === 'string'
                ? toMoney(Number(fieldsValues.nominalValue))
                : fieldsValues.nominalValue
              : null,
          issuancePrice: fieldsValues.issuancePrice
            ? typeof fieldsValues.issuancePrice === 'number' || typeof fieldsValues.issuancePrice === 'string'
              ? toMoney(Number(fieldsValues.issuancePrice))
              : fieldsValues.issuancePrice
            : null,
          ewpg: fieldsValues.ewpg === 'true',
          publicationRestricted: fieldsValues.publicationRestricted === 'true',
        };

        if (dateAndTime) {
          params.lockInTime = dateAndTime;
        }
        if (fieldsValues.isin.length === 0) {
          params.isin = null;
        }
        if (fieldsValues.ewpg === 'true') {
          delete params.backupWallet;
        }
        if (fieldsValues.issuanceDocument?.file) {
          const docResponse = await documentsApi.adminDocumentsCreate({
            file: fieldsValues.issuanceDocument?.file,
          });
          params.issuanceDocumentId = docResponse.id;
        }

        const product = await api.adminProductsCreate({ adminProductCreationRequest: params });
        setSuccessView(true);
        setProductCreated(product);
        onAddNewProduct();
      });
    },
    [withApi, api],
  );

  const onStart = useCallback(() => {
    setInfoView(false);
  }, []);

  return (
    <AddProductModal
      isInfoView={isInfoView}
      isExitingView={isExitingView}
      isSuccessView={isSuccessView}
      productCreated={productCreated}
      onSubmit={onSubmit}
      onClose={onCloseModal}
      modalHeader={modalHeader}
      actionButtons={actionButtons}
      error={error}
    />
  );
};

export default AddProduct;
